import React, { useEffect, useState } from "react";
import StoreLocationSelect from "../../ui/StoreLocationSelect";
import CustomSelect from "../../ui/CustomSelect";
import Calendar from "../Calendar";
import MeasurementImage from "../../../assets/image/Measurement.svg";
import PlanningImage from "../../../assets/image/Planning.svg";
import InteriorDesignImage from "../../../assets/image/InteriorDesign.svg";
import { ResidenceType, TypeofRoom, RoomQuantities } from "../../../constants/dropdownOptions";

const isInteriorDesign = (service) =>
  service?.ServiceName?.toLowerCase().includes('interior design');

export default function AppointmentStep3({
  Services,
  setTotalPrice,
  selectedService,
  selectedStore,
  handleStoreLocationChange,
  StoreLocation,
  NumberofRooms,
  selectedNumberofRooms,
  handleNumberofRoomsChange,
  ServiceMode,
  selectedServiceMode,
  handleServiceModeChange,
  selectedDate,
  selectedSlot,
  setSelectedDate,
  MeasurementServiceDateforPlanning,
  setSelectedSlot,
  randomID,
  idResidenceType,
  setIdResidenceType,
  idRooms,
  setIdRooms,
  refs: {
    storeRef,
    numberofRoomsRef,
    serviceModeRef,
    calendarRef,
  },
}) {
  const [showMoreThan4Popup, setShowMoreThan4Popup] = useState(false);

  useEffect(() => {
    if (
      selectedService?.ServiceName === "Measurement Service" &&
      selectedNumberofRooms &&
      selectedService?.Price
    ) {
      const roomCount = parseInt(
        selectedNumberofRooms?.value || selectedNumberofRooms,
        10
      );
      const unitPrice = parseFloat(selectedService.Price);

      const total =
        unitPrice + Math.max(0, roomCount - 1) * 50;

      setTotalPrice(total);
    } else if (
      selectedService?.ServiceName === "Kitchen Planning Service" &&
      selectedService?.Price
    ) {
      setTotalPrice(parseFloat(selectedService.Price));
    } else {
      setTotalPrice(null);
    }
  }, [selectedNumberofRooms, selectedService, setTotalPrice]);

  useEffect(() => {
    if (selectedNumberofRooms?.value === "More than 4") {
      setShowMoreThan4Popup(true);
    } else {
      setShowMoreThan4Popup(false);
    }
  }, [selectedNumberofRooms]);

  const shouldShowCalendar = () => {
    if (!selectedService || !selectedStore) return false;

    if (isInteriorDesign(selectedService)) {
      return (
        !!idResidenceType &&
        idRooms.length > 0 &&
        idRooms.every((r) => r.roomType && r.quantity)
      );
    }

    if (selectedService.ServiceName === "Measurement Service") {
      return (
        selectedNumberofRooms?.value &&
        selectedNumberofRooms?.value !== "More than 4"
      );
    }

    if (selectedService.ServiceName === "Kitchen Planning Service") {
      return !!MeasurementServiceDateforPlanning;
    }

    return true;
  };

  const handleAddRoom = () => {
    setIdRooms((prev) => [...prev, { roomType: '', quantity: 1 }]);
  };

  const handleRemoveRoom = (index) => {
    setIdRooms((prev) => prev.filter((_, i) => i !== index));
  };

  const handleRoomChange = (index, field, value) => {
    setIdRooms((prev) =>
      prev.map((row, i) => (i === index ? { ...row, [field]: value } : row))
    );
  };

  const getServiceImage = (serviceName) => {
    if (serviceName.toLowerCase().includes("measurement")) {
      return MeasurementImage;
    } else if (serviceName.toLowerCase().includes("planning")) {
      return PlanningImage;
    } else if (serviceName.toLowerCase().includes("interior design")) {
      return InteriorDesignImage;
    }
    return null;
  };

  return (
    <div className="step-3">
      <div className="select-info">
        {Services
          .filter((s) => s.ServiceName === selectedService.ServiceName)
          .map((service, index) => (
            <React.Fragment key={service.ServiceCode || index}>
              <div className="service-header-row">
                <h4>{service.ServiceName}</h4>

                {getServiceImage(service.ServiceName) && (
                  <img
                    src={getServiceImage(service.ServiceName)}
                    alt={service.ServiceName}
                    className="service-image"
                  />
                )}

                <div className="service-meta">
                  <div className="price-container">
                    <div className="price">
                      <span className="currency-sup">Dhs</span>
                      {service.Price?.toString().includes(".") ? (
                        <>
                          {service.Price.toString().split(".")[0]}
                          <span className="decimal-sub">
                            .{service.Price.toString().split(".")[1]}
                          </span>
                        </>
                      ) : (
                        service.Price
                      )}
                    </div>

                    {service.ServiceName.toLowerCase().includes("measurement") && (
                      <>
                        <div className="price-sub">
                          for the first room
                        </div>
                        <div className="price-sub light">
                          + Dhs 50 per additional room
                        </div>
                      </>
                    )}
                  </div>
                </div>
              </div>

              <p>{service.Description}</p>
            </React.Fragment>
          ))}
      </div>

      <div className="appointment-info">
        <div className="form-group input">
          <label className="required">Select a Store</label>
          <StoreLocationSelect
            ref={storeRef}
            options={StoreLocation}
            value={selectedStore}
            onChange={handleStoreLocationChange}
          />
        </div>

        {isInteriorDesign(selectedService) && (
          <>
            <div className="form-group input">
              <label className="required">Type of Residence</label>
              <CustomSelect
                options={ResidenceType}
                value={idResidenceType ? ResidenceType.find((r) => r.value === idResidenceType) : null}
                onChange={(opt) => setIdResidenceType(opt?.value || '')}
                placeholder="Select Residence Type"
              />
            </div>

            <div className="form-group input">
              <label className="required">Room Types</label>
              <div className="room-subform">
                {idRooms.map((row, index) => (
                  <div className="room-subform-row" key={index}>
                    <CustomSelect
                      options={TypeofRoom}
                      value={TypeofRoom.find((t) => t.value === row.roomType) || null}
                      onChange={(opt) => handleRoomChange(index, 'roomType', opt?.value || '')}
                      placeholder="Room Type"
                    />
                    <CustomSelect
                      options={RoomQuantities}
                      value={RoomQuantities.find((q) => q.value === row.quantity) || null}
                      onChange={(opt) => handleRoomChange(index, 'quantity', opt?.value || 1)}
                      placeholder="Qty"
                    />
                    {idRooms.length > 1 && (
                      <button
                        type="button"
                        className="remove-room-btn"
                        onClick={() => handleRemoveRoom(index)}
                      >
                        &#x2715;
                      </button>
                    )}
                  </div>
                ))}
                <button type="button" className="add-room-btn" onClick={handleAddRoom}>
                  + Add Room Type
                </button>
              </div>
            </div>
          </>
        )}

        {selectedService.ServiceName === "Measurement Service" && (
          <div className="form-group input">
            <label className="required">Number of Rooms</label>

            <CustomSelect
              ref={numberofRoomsRef}
              options={NumberofRooms}
              value={selectedNumberofRooms}
              onChange={handleNumberofRoomsChange}
            />

            {selectedNumberofRooms &&
              selectedNumberofRooms?.value !== "More than 4" &&
              selectedService?.Price && (
                <p className="total-price">
                  <strong>Total Price: </strong>
                  <span className="price">
                    <span className="currency-sup">Dhs</span>

                    {(() => {
                      const roomCount = parseInt(
                        selectedNumberofRooms?.value ||
                          selectedNumberofRooms,
                        10
                      );
                      const unitPrice = parseFloat(
                        selectedService.Price
                      );

                      const total = (
                        unitPrice +
                        Math.max(0, roomCount - 1) * 50
                      ).toFixed(2);

                      const [whole, decimal] = total.split(".");

                      return (
                        <>
                          {whole}
                          {decimal && (
                            <span className="decimal-sub">
                              .{decimal}
                            </span>
                          )}
                        </>
                      );
                    })()}
                  </span>
                </p>
              )}
          </div>
        )}

        {selectedService.ServiceName === "Kitchen Planning Service" && (
          <div className="form-group input">
            <label className="required">Service Mode</label>
            <CustomSelect
              ref={serviceModeRef}
              options={ServiceMode}
              value={selectedServiceMode}
              onChange={handleServiceModeChange}
            />
          </div>
        )}

        {selectedService &&
          selectedStore &&
          (selectedService.ServiceName === "Kitchen Planning Service" &&
          !MeasurementServiceDateforPlanning ? (
            <p className="reference-pending-message">
              Please enter your phone number on the previous step to unlock available dates.
            </p>
          ) : (
            shouldShowCalendar() && (
              <Calendar
                ref={calendarRef}
                selectedDate={selectedDate}
                setSelectedDate={setSelectedDate}
                selectedTime={selectedSlot}
                setSelectedSlot={setSelectedSlot}
                serviceType={selectedService}
                storeLocation={selectedStore}
                uniqueID={randomID}
                MeasurementServiceDateforPlanning={
                  MeasurementServiceDateforPlanning
                }
              />
            )
          ))}

        <p className="notes">
          Additional dates and times are released daily. Alternatively call
          <a href="tel:800 4532"> 800 4532 </a>
          to speak to us.
        </p>
      </div>

      {showMoreThan4Popup && (
        <div className="dialog-overlay">
          <div className="dialog-box">
            <h3>Booking Unavailable</h3>
            <p>
              Please contact our <strong>Service Center</strong> at{" "}
              <a href="tel:8004532">800 4532</a>
            </p>
            <button
              className="dialog-close-button"
              onClick={() => {
                setShowMoreThan4Popup(false);
                handleNumberofRoomsChange(null);
              }}
            >
              OK
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
